/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { FaFacebook, FaLinkedin, FaInstagram, FaTwitter, FaEye } from "react-icons/fa";
import { motion } from "framer-motion";
import axios from "axios";

const Footer = () => {
  // State to store API data
  const [settings, setSettings] = useState(null);
  const [footerLogo, setFooterLogo] = useState(null);
  const [visitCount, setVisitCount] = useState(null);
  const [animatedCount, setAnimatedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [counterStarted, setCounterStarted] = useState(false);
  const counterRef = useRef(null);
  
  // Function to handle scrolling to top on navigation
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // API configuration
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/settings/ui`;
  const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;
  const VISIT_API = `${process.env.REACT_APP_API_BASE_URL}/UI/visit-count`;

  // Function to fetch image from media API
  const fetchFooterLogo = async (mediaId) => {
    if (!mediaId) return null;
    
    try {
      // Get the actual media ID (could be object or string)
      const id = typeof mediaId === 'object' ? mediaId.id || mediaId._id : mediaId;
      if (!id) return null;
      
      const response = await axios.get(`${API_MEDIA}/${id}/download`, {
        responseType: "blob",
      });
      
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error("Error fetching footer logo:", error);
      return null;
    }
  };

  // Animate counter function - EXACTLY LIKE YOUR IMPACT VIEWER
  const animateCounter = (target) => {
    if (!target || target === 0) return;
    
    const duration = 2000; // 2 seconds like your impact viewer
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4); // Same easing function
      const currentValue = Math.floor(ease * target);

      setAnimatedCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setAnimatedCount(target); // Final exact value
      }
    };

    requestAnimationFrame(step);
  };

  // Function to format large numbers with abbreviations
  const formatLargeNumber = (num) => {
    if (!num) return "0";
    
    // For extremely large numbers, use scientific notation
    if (num >= 1e15) {
      return num.toExponential(2);
    }
    
    // For large but manageable numbers, use locale string
    return num.toLocaleString('en-US', {
      maximumFractionDigits: 0
    });
  };

  // Get font size based on number length
  const getCounterFontSize = (num) => {
    const numStr = num.toString();
    const length = numStr.length;
    
    if (length <= 6) return "text-5xl md:text-6xl";
    if (length <= 9) return "text-4xl md:text-5xl";
    if (length <= 12) return "text-3xl md:text-4xl";
    return "text-2xl md:text-3xl";
  };

  // Intersection Observer for counter animation
  useEffect(() => {
    if (!counterRef.current || !visitCount || counterStarted) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCounterStarted(true);
            animateCounter(visitCount);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -50px 0px'
      }
    );
    
    observer.observe(counterRef.current);
    
    return () => {
      if (observer) observer.disconnect();
    };
  }, [visitCount, counterStarted]);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch settings data
        const response = await axios.get(API_URL);
        const apiData = response.data;
        
        // Find the active setting
        let setting = null;
        
        if (apiData.results && Array.isArray(apiData.results)) {
          setting = apiData.results[0] || apiData.results.find(s => s.isActive);
        } else if (Array.isArray(apiData)) {
          setting = apiData[0] || apiData.find(s => s.isActive);
        } else if (typeof apiData === 'object') {
          setting = apiData;
        }
        
        if (setting) {
          setSettings(setting);
          
          // Fetch footer logo if available
          if (setting.footerLogoMediaId) {
            const logoUrl = await fetchFooterLogo(setting.footerLogoMediaId);
            setFooterLogo(logoUrl);
          }
        }
        
        // Fetch visitor count
        try {
          const visitRes = await axios.get(VISIT_API);
          setVisitCount(visitRes.data.totalVisits);
        } catch (visitError) {
          console.error("Error fetching visitor count:", visitError);
        }
        
      } catch (error) {
        console.error("❌ Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Cleanup function for blob URL
    return () => {
      if (footerLogo) {
        URL.revokeObjectURL(footerLogo);
      }
    };
  }, []);

  // Show loading state
  if (loading) {
    return (
      <footer className="bg-gradient-to-r from-gray-900 to-black text-white py-8 px-6 shadow-lg">
        <div className="container mx-auto text-center py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-800 rounded w-1/4 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-800 rounded w-3/4 mx-auto mb-6"></div>
            <div className="h-4 bg-gray-800 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gradient-to-r from-gray-900 to-black text-white py-8 px-6 shadow-lg">
      {/* Website Visitors Banner - FIXED RESPONSIVE LAYOUT */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-10"
        ref={counterRef}
      >
        <div className="container mx-auto " >
          <div className="w-full">
            <div className="flex flex-col items-center">
              <div className="relative w-full max-w-4xl  ">
                {/* Main counter card - RESPONSIVE FLEXBOX */}
                <motion.div 
                  className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white rounded-xl  md:p-2 shadow-lg border-l-0 md:border-l-8 border-orange-500"
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {/* Left Section: Icon and Title */}
                  <div className="flex items-center gap-4 w-full md:w-auto justify-center md:justify-start">
                    {/* Icon Circle */}
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full border-4 border-gray-900 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <FaEye className="text-gray-500 text-xl md:text-2xl" />
                    </div>
                    
                    {/* Title */}
                    <div className="text-lg md:text-2xl font-medium text-gray-900 text-center md:text-left">
                      Total Website Visitors
                    </div>
                  </div>

                  {/* Middle Divider - Only on desktop */}
                  <div className="hidden md:block w-2 h-10 bg-gray-900"></div>

                  {/* Right Section: Counter */}
                  <div className="w-full md:w-auto">
                    <motion.div 
                      key={animatedCount}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className={`${getCounterFontSize(animatedCount)} font-black text-gray-900 text-center md:text-right break-all  px-2`}
                    >
                      {formatLargeNumber(animatedCount)}
                    </motion.div>
                    
                    {/* Counter label for mobile */}
                    <div className="md:hidden text-center text-sm text-gray-600 mt-1">
                      Total Visitors
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Footer Content */}
      <div className="container mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
        {/* Logo & Description - DYNAMIC */}
        <div className="space-y-3">
          {/* Dynamic Website Name */}
          <h2 className="text-2xl font-extrabold tracking-tight">
            {settings?.websiteName}
          </h2>
          
          {/* Dynamic Description */}
          <p className="text-gray-400 text-sm">
            {settings?.description}
          </p>
          
          <div className="flex items-center space-x-2 pt-2">
            {/* Dynamic Footer Logo */}
            {footerLogo ? (
              <motion.img 
                src={footerLogo}
                alt={`${settings?.websiteName} Footer Logo`}
                className="h-12 w-12 object-contain"
                whileHover={{ rotate: 5, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
                onError={(e) => {
                  console.error("Failed to load footer logo");
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="h-12 w-12 bg-gray-800 rounded flex items-center justify-center">
                <span className="text-gray-400 text-xs">Logo</span>
              </div>
            )}
            
            {/* Dynamic Invitation Text */}
            <p className="text-gray-400 text-sm">
              An initiative by{" "}
              {settings?.invitationByLink ? (
                <a
                  href={settings.invitationByLink}
                  className="text-teal-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {settings?.invitationByName}
                </a>
              ) : (
                <span className="text-teal-400">
                  {settings?.invitationByName}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Quick Links - Two Columns */}
        <div>
          <h3 className="text-lg font-semibold border-b border-gray-700 pb-1 mb-3">Quick Links</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <Link 
              to="/about" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 flex items-center gap-1 group"
              onClick={handleLinkClick}
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              About Us
            </Link>
            <Link 
              to="/events" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 flex items-center gap-1 group"
              onClick={handleLinkClick}
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Events
            </Link>
            <Link 
              to="/membership" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 flex items-center gap-1 group"
              onClick={handleLinkClick}
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Membership
            </Link>
            <Link 
              to="/ContactUs" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 flex items-center gap-1 group"
              onClick={handleLinkClick}
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Contact Us
            </Link>
            <a
              href="/MASAAwards"
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 col-span-2 md:col-span-1 flex items-center gap-1 group"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Masa Awards
            </a>
            <a
              href="https://www.mantrasworld.com/"
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 col-span-2 md:col-span-1 flex items-center gap-1 group"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Mantra's World
            </a>
            <a
              href="https://mantrasfoundation.org/"
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 col-span-2 md:col-span-1 flex items-center gap-1 group"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Mantras Foundation
            </a>
            <Link 
              to="/TermsAndConditions" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 flex items-center gap-1 group"
              onClick={handleLinkClick}
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Terms & Conditions
            </Link>
            <Link 
              to="/PrivacyPolicy" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 flex items-center gap-1 group"
              onClick={handleLinkClick}
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Privacy Policy
            </Link>
            <Link 
              to="/CancellationAndRefund" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 flex items-center gap-1 group"
              onClick={handleLinkClick}
            >
              <span className="w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
              CancellationAndRefund
            </Link>
          </div>
        </div>

        {/* Contact Information - DYNAMIC */}
        <div>
          <h3 className="text-lg font-semibold border-b border-gray-700 pb-1 mb-3">Contact Us</h3>
          
          {/* Dynamic Email */}
          {settings?.contactUs?.email ? (
            <motion.p 
              className="text-gray-400 text-sm flex items-center gap-2 mb-2 group"
              whileHover={{ x: 5 }}
            >
              <span className="text-teal-500">📧</span>
              <a 
                href={`mailto:${settings.contactUs.email}`}
                className="hover:text-teal-400 transition-colors duration-300 break-all"
              >
                {settings.contactUs.email}
              </a>
            </motion.p>
          ) : (
            <p className="text-gray-400 text-sm">
              Email:{" "}
              <span className="text-gray-500">Not provided</span>
            </p>
          )}
          
          {/* Dynamic Phone */}
          {settings?.contactUs?.phone ? (
            <motion.p 
              className="text-gray-400 text-sm flex items-center gap-2 group"
              whileHover={{ x: 5 }}
            >
              <span className="text-teal-500">📞</span>
              <a 
                href={`tel:${settings.contactUs.phone.replace(/\D/g, '')}`}
                className="hover:text-teal-400 transition-colors duration-300"
              >
                {settings.contactUs.phone}
              </a>
            </motion.p>
          ) : (
            <p className="text-gray-400 text-sm mt-1">
              Phone:{" "}
              <span className="text-gray-500">Not provided</span>
            </p>
          )}
        </div>

        {/* Social Media Links - DYNAMIC */}
        <div>
          <h3 className="text-lg font-semibold border-b border-gray-700 pb-1 mb-3">Follow Us</h3>
          <div className="flex space-x-4 justify-center md:justify-start">
            
            {/* Facebook - Dynamic */}
            {settings?.socialMedia?.facebook && (
              <motion.a 
                href={settings.socialMedia.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-600 transition-colors duration-300 relative group"
                title="Follow us on Facebook"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaFacebook size={24} />
                <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Facebook
                </span>
              </motion.a>
            )}
            
            {/* LinkedIn - Dynamic */}
            {settings?.socialMedia?.linkedin && (
              <motion.a 
                href={settings.socialMedia.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-700 transition-colors duration-300 relative group"
                title="Follow us on LinkedIn"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaLinkedin size={24} />
                <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  LinkedIn
                </span>
              </motion.a>
            )}
            
            {/* Instagram - Dynamic */}
            {settings?.socialMedia?.instagram && (
              <motion.a 
                href={settings.socialMedia.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-pink-600 transition-colors duration-300 relative group"
                title="Follow us on Instagram"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaInstagram size={24} />
                <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Instagram
                </span>
              </motion.a>
            )}
            
            {/* Twitter - Dynamic */}
            {settings?.socialMedia?.twitter && (
              <motion.a 
                href={settings.socialMedia.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-400 transition-colors duration-300 relative group"
                title="Follow us on Twitter/X"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTwitter size={24} />
                <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Twitter
                </span>
              </motion.a>
            )}
            
            {/* Show message if no social media links */}
            {(!settings?.socialMedia?.facebook && 
              !settings?.socialMedia?.linkedin && 
              !settings?.socialMedia?.instagram && 
              !settings?.socialMedia?.twitter) && (
              <p className="text-gray-500 text-sm">
                No social media links available
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Copyright - DYNAMIC */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="border-t border-gray-800 mt-8 pt-4 text-center text-gray-500 text-sm"
      >
        {settings?.footerCopyrightContent ? (
          <div dangerouslySetInnerHTML={{ __html: settings.footerCopyrightContent }} />
        ) : (
          <p>
            © {new Date().getFullYear()} {settings?.websiteName || "MASA Forum"}. All rights reserved.
          </p>
        )}
      </motion.div>
    </footer>
  );
};

export default Footer;